import * as Dialog from '@radix-ui/react-dialog';
import type { ConnectedWallet } from '@privy-io/react-auth';
import { Dispatch, SetStateAction, useCallback, useRef } from 'react';
import { useClickAway, useMedia } from 'react-use';

import { MatchaConnectedWallet } from '@/hooks/useMatchaWallets';

import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { fontStyles } from '@/styles/typography';
import { breakpoints } from '@/styles/util';

import { BottomPanelModal } from '@/ui/Modal/BottomPanelModal';
import { Popover } from '@/ui/Popover';

import { ChainAddress } from '@/utils/models';

import { RecipientInput } from './RecipientInput';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  chainId: number;
  connectedWallet: MatchaConnectedWallet<ConnectedWallet> | undefined;
}

export const RecipientModal = ({
  isOpen,
  onOpenChange,
  onClose,
  chainId,
  connectedWallet,
}: Props) => {
  const isMobile = useMedia(breakpoints.mobile, false);
  const { recipientAddress, setRecipientAddress } =
    useMarketTradeSettingsStore(recipientAddressSelector);

  const popoverRef = useRef<HTMLDivElement>(null!);
  const inputRef = useRef<HTMLInputElement>(null!);

  useClickAway(popoverRef, () => {
    if (!isOpen || isMobile) return;
    onOpenChange(false);
  });

  const handleReset = useCallback(() => {
    setRecipientAddress(undefined);
  }, [setRecipientAddress]);

  const handleConfirm = useCallback(
    (address: ChainAddress) => {
      setRecipientAddress(address);
    },
    [setRecipientAddress],
  );

  const content = (
    <RecipientInput
      chainId={chainId}
      currentAddress={recipientAddress}
      onConfirm={handleConfirm}
      onReset={handleReset}
      onClose={onClose}
      inputRef={inputRef}
      connectedWallet={connectedWallet}
      hasCustomRecipient={!!recipientAddress}
    />
  );

  return isMobile ? (
    <BottomPanelModal isOpen={isOpen} onOpenChange={onOpenChange} onClose={onClose} variant="LARGE">
      <div className="p-6">
        <Dialog.Title className={fontStyles({ variant: 'H5', class: 'mb-1' })}>
          Receiving Wallet
        </Dialog.Title>
        {content}
      </div>
    </BottomPanelModal>
  ) : isOpen ? (
    <Popover
      className=" bg-special-background-modal absolute h-auto rounded-[1.125rem] z-100 shadow-25 bottom-11 right-0 w-[344px]"
      popoverRef={popoverRef}
      isOpen={isOpen}
      onClose={onClose}
    >
      {content}
    </Popover>
  ) : null;
};
